import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { BedDouble, CalendarRange, Home as HomeIcon, ShieldCheck, Plus } from "lucide-react";
import { hostService } from "@/lib/services";
import { formatUsh } from "@/lib/format";
import type { Booking, Listing, Property } from "@/types/api";

export const Route = createFileRoute("/_authenticated/host/")({
  component: HostOverview,
});

function unwrap<T>(res: unknown): T[] {
  if (!res) return [];
  const r = res as { data?: T[] };
  return Array.isArray(r.data) ? r.data : [];
}

function HostOverview() {
  const propsQ = useQuery({
    queryKey: ["host-properties"],
    queryFn: () => hostService.properties(),
  });
  const listingsQ = useQuery({
    queryKey: ["host-listings"],
    queryFn: () => hostService.listings(),
  });
  const bookingsQ = useQuery({
    queryKey: ["host-bookings", { per_page: 5 }],
    queryFn: () => hostService.bookings({ per_page: 5 }),
  });

  const properties = unwrap<Property>(propsQ.data);
  const listings = unwrap<Listing>(listingsQ.data);
  const bookings = bookingsQ.data?.data ?? [];

  const verified = properties.filter((p) => p.verified).length;
  const pendingBookings = bookings.filter((b: Booking) => b.status === "pending").length;

  const stats = [
    { icon: HomeIcon, label: "Properties", value: properties.length, hint: `${verified} verified` },
    { icon: BedDouble, label: "Listings", value: listings.length },
    {
      icon: CalendarRange,
      label: "Recent bookings",
      value: bookings.length,
      hint: `${pendingBookings} pending`,
    },
    { icon: ShieldCheck, label: "Verified", value: verified, hint: `of ${properties.length}` },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-ink">Host overview</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Manage your properties, listings, and bookings.
          </p>
        </div>
        <Link
          to="/host/properties/new"
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90"
        >
          <Plus className="h-4 w-4" /> Add property
        </Link>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-subtle">
                  {s.label}
                </p>
                <Icon className="h-4 w-4 text-ink-muted" />
              </div>
              <p className="mt-2 text-2xl font-extrabold text-ink">{s.value}</p>
              {s.hint && <p className="mt-1 text-xs text-ink-muted">{s.hint}</p>}
            </div>
          );
        })}
      </div>

      <section className="mt-8 grid gap-6 lg:grid-cols-2">
        <Card title="Recent bookings" linkTo="/host/bookings" linkLabel="See all">
          {bookingsQ.isLoading ? (
            <Skeleton rows={3} />
          ) : bookings.length === 0 ? (
            <Empty text="No bookings yet." />
          ) : (
            <ul className="divide-y divide-border">
              {bookings.slice(0, 5).map((b: Booking) => (
                <li key={b.id} className="flex items-center justify-between py-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-bold text-ink">{b.property?.name ?? "Stay"}</p>
                    <p className="text-xs text-ink-muted">
                      {b.check_in_date} → {b.check_out_date} · {formatUsh(b.total)}
                    </p>
                  </div>
                  <span className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-ink-muted">
                    {b.status.replace("_", " ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Your listings" linkTo="/host/listings" linkLabel="Manage">
          {listingsQ.isLoading ? (
            <Skeleton rows={3} />
          ) : listings.length === 0 ? (
            <Empty text="No listings yet. Create a property first, then add listings to it." />
          ) : (
            <ul className="divide-y divide-border">
              {listings.slice(0, 5).map((l) => (
                <li key={l.id} className="flex items-center justify-between py-3 text-sm">
                  <div className="min-w-0">
                    <p className="truncate font-bold text-ink">{l.title}</p>
                    <p className="text-xs text-ink-muted">{l.property?.name ?? "—"}</p>
                  </div>
                  <span className="text-xs font-bold text-ink">
                    {formatUsh(l.price_per_night)}/night
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}

function Card({
  title,
  linkTo,
  linkLabel,
  children,
}: {
  title: string;
  linkTo: "/host/bookings" | "/host/listings" | "/host/properties";
  linkLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-extrabold tracking-tight text-ink">{title}</h2>
        <Link to={linkTo} className="text-xs font-bold text-primary hover:underline">
          {linkLabel} →
        </Link>
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-xl brand-skeleton" />
      ))}
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="py-6 text-center text-sm text-ink-muted">{text}</p>;
}
